import { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Grid, Typography, Tooltip, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, FormGroup } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { regexConstants } from "../../../constants/regEx";
import { numberWithCommas } from "../../../utils";
import DailyRentWarningModal from "./DailyRentWarningModal";

export default function FixedTableConditions({ data, onFieldChange, error, setError, modeType, originalFixedRate }) {
  const [dailyRentWarning, setDailyRentWarning] = useState(false);
  const { leaseBasicDetails } = useSelector((state) => state.leases);
  const isDailyRentEnabled = !!data?.daily_rent;
  const fixedTableRows = [
    { key: "fixed", label: "Fixed" },
    ...(isDailyRentEnabled ? [{ key: "daily", label: "Daily" }] : []),
    { key: "fc_rate", label: "Flight Cycles" },
    { key: "fh_rate", label: "Flight Hours" },
  ];
  const validationMessage = isDailyRentEnabled
    ? "Enter a Daily Rate and at least one of Flight Cycles or Flight Hours."
    : "Enter a Fixed Rate and at least one of Flight Cycles or Flight Hours.";
  const handleDailyRentToggle = (checked) => {
    const isActiveLease = leaseBasicDetails?.status && leaseBasicDetails?.status !== "Draft";
    if (checked && isActiveLease) {
      setDailyRentWarning(true);
    } else {
      onFieldChange("daily_rent", checked);
      if (!checked) {
        onFieldChange("fixed_table_condition", { daily: "", fixed: originalFixedRate ?? "" });
      }
    }
  };
  return (
    <>
      {data?.is_pbh_fixed_table ? <Box sx={{ width: "100%" }}>
        <FormGroup className='disabled-checkbox-wrapper'>
          <Box style={{ display: 'flex', alignItems: 'center', marginLeft: "-12px", cursor: modeType === "view" ? "not-allowed" : "pointer" }}>
            <Checkbox
              checked={isDailyRentEnabled}
              onChange={(e) => handleDailyRentToggle(e.target.checked)}
              disabled={modeType === 'view'}
              disableRipple
            />
            <span className="check-item-span">Enable Daily Rent</span>
          </Box>
        </FormGroup>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 1, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          Fixed Table Conditions
          <Tooltip title={validationMessage}>
            <Box component="span" sx={{ display: "inline-flex", cursor: "pointer" }}>
              <InfoIcon fontSize="8px" color="primary" />
            </Box>
          </Tooltip>
        </Typography>
        <TableContainer className='scrollable-table-container'>
          <Table size="small" className="mui-table-format">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: '100px !important' }}> Type</TableCell>
                {fixedTableRows.map((row) => (
                  <TableCell key={row.key}>
                    {row.label}{(row.key === 'daily' || (row.key === 'fixed' && !isDailyRentEnabled)) ? '*' : ""}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell> Rate</TableCell>
                {fixedTableRows.map((row) => {
                  const isFixedDisabled = row.key === 'fixed' && isDailyRentEnabled;
                  return (
                    <TableCell key={row.key}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: 200, mx: "auto" }}
                        value={modeType === 'view' && data?.fixed_table_condition?.[row.key] !== '' && data?.fixed_table_condition?.[row.key] !== null && data?.fixed_table_condition?.[row.key] !== undefined ? numberWithCommas(data?.fixed_table_condition?.[row.key]) : (data?.fixed_table_condition?.[row.key] ?? "")}
                        onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) || e.target.value === "" ? onFieldChange("fixed_table_condition", { [row.key]: e.target.value }) : e.preventDefault(); }}
                        onFocus={() => setError(prev => ({ ...prev, fixed_table_condition: { ...prev?.fixed_table_condition, [row.key]: "" } }))}
                        error={Boolean(error?.fixed_table_condition?.[row.key])}
                        helperText={error?.fixed_table_condition?.[row.key]}
                        disabled={modeType === 'view' || isFixedDisabled}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">{data?.currency?.symbol}</InputAdornment>) } }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box> : null}
      {data?.fixed_rate ? <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ md: 3, xs: 12 }}>
          <TextField
            required
            id="amount"
            value={modeType === 'view' && data?.amount !== '' && data?.amount !== null && data?.amount !== undefined ? numberWithCommas(data?.amount) : (data?.amount ? data?.amount : '')}
            onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('amount', e.target.value) : e.preventDefault() }}
            label="Rate / Unit"
            variant="outlined"
            size="small"
            margin="normal"
            error={!!error?.amount}
            helperText={error?.amount}
            onFocus={() => setError(prev => ({ ...prev, amount: "" }))}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">{data?.currency?.symbol}</InputAdornment>) } }}
            fullWidth
            disabled={modeType === 'view'}
          />
        </Grid>
      </Grid> : null}
      <DailyRentWarningModal
        open={dailyRentWarning}
        onCancel={() => setDailyRentWarning(false)}
        onProceed={() => { onFieldChange("daily_rent", true); onFieldChange("fixed_table_condition", { fixed: "" }); setDailyRentWarning(false); }}
      />
    </>
  );
}
